'use client';

import Button from '@egalite/ui/Buttons';
import Section from '@egalite/ui/IncluiPCD/Section';
import SectionTitle from '@egalite/ui/IncluiPCD/SectionTitle';
import Typography from '@egalite/ui/Typography';
import * as Tabs from '@radix-ui/react-tabs';
import dynamic from 'next/dynamic';
import { FC, useState } from 'react';

// add-to-calendar-button é um pacote de ~1 MB e o botão (web component) só
// materializa no cliente e apenas para eventos futuros (pastDateHandling
// 'hide'). Carregado sob demanda para ficar fora do bundle inicial da rota.
const AddToCalendarButton = dynamic(
	() => import('add-to-calendar-button-react').then(m => m.AddToCalendarButton),
	{ ssr: false },
);

type Props = {
	data?: any[];
};

const createDate = (date: string) => {
	const year = new Date().getFullYear();
	const [day, month] = date.split('/');
	return `${year}-${month}-${day}`;
};

const addDuration = (time: string, duration: number) => {
	const [hour, minute] = time.split(':');
	const date = new Date();
	date.setHours(Number(hour));
	date.setMinutes(Number(minute));
	date.setMinutes(date.getMinutes() + duration);
	return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
};

const activeDay = () => {
	const date = new Date();
	const day = date.getDate().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const year = date.getFullYear();
	return `${day}/${month}`;
}

const showCalendar = (day: string, time: string) => {
	// if day and time is in the past, don't show calendar
	const date = new Date();
	date.setHours(date.getUTCHours());
	date.setDate(date.getUTCDate());
	const dayParts = day.split('/');
	const hourParts = time.split(':');
	const year = new Date().getFullYear();
	const parsedDate = new Date(
		year, 
		Number(dayParts[1]) - 1, 
		Number(dayParts[0]), 
		Number(hourParts[0]) + 3, // add 3 hours to UTC
		Number(hourParts[1])
	);
	return parsedDate > date;
}
const Schedule: FC<Props> = ({ data }) => {

	if (!data || !Array.isArray(data)) {
		return null;
	}

	return (
		<Section>
			<SectionTitle>Programação 2026</SectionTitle>

			<div className="max-w-7xl w-full text-center my-16">
				<Typography variant="body-1" className="text-base-grey-5">
					Os painéis serão transmitidos no site da IncluiPcD de forma gratuita e sem a necessidade de cadastro. Basta
					acessar a nossa home e aproveitar os conteúdos. Você poderá acompanhar também pelo nosso canal do Youtube
					<a href="https://www.youtube.com/@incluipcd" className="underline text-brand-main">
						{' '}
						nesse link{' '}
					</a>
					e ter acesso aos conteúdos das edições passadas.
				</Typography>
			</div>

			<div className="max-w-7xl w-full">
				<Tabs.Root defaultValue={data.map(d => d.day).find(day => day === activeDay()) ?? data[0].day}>
					<Tabs.List className="flex flex-row justify-center items-center gap-4 md:gap-8 mb-12">
						{data.map((day, idx) => (
							<Tabs.Trigger
								key={idx}
								value={day.day}
								className="rounded-3xl p-4 py-8 md:p-12 md:py-12 text-3xl font-bold text-brand-main bg-white data-[state=active]:bg-brand-main data-[state=active]:text-white border-solid border border-transparent hover:border-brand-main transition-colors duration-300 ease-in-out shadow-2xl"
							>
								{day.day}
							</Tabs.Trigger>
						))}
					</Tabs.List>
					{data.map((day, idx) => (
						<Tabs.Content
							key={idx}
							value={day.day}
							className="z-20 bg-white px-16 py-4 rounded-3xl shadow-2xl  border-t border-solid border-base-grey-3"
						>
							{day.talks.map((talk, idx) => (
								<div key={idx} className="my-16">
									{talk.sponsor ? (
										<Typography
											variant="body-2"
											className="text-base-grey-5 font-bold uppercase tracking-widest mb-2"
										>
											{talk.sponsor}
										</Typography>
									) : null}
									<Typography variant="headline-4">{talk.title}</Typography>
									<Typography variant="body-1" className="text-brand-main font-bold">
										{talk.time}
									</Typography>
									{talk.speakers.map((speaker, idx) => (
										<Typography variant="body-1" key={idx}>
											{speaker}
										</Typography>
									))}
									<div className="mt-2 -ml-2">
										{showCalendar(day.day, talk.time) ? (
											<AddToCalendarButton
												name={talk.title}
												startDate={createDate(day.day)}
												startTime={addDuration(talk.time, 0)}
												endTime={addDuration(talk.time, 60)}
												timeZone="America/Sao_Paulo"
												location="https://www.incluipcd.com.br/programacao"
												options={['Google', 'Outlook.com', 'iCal|Arquivo para todos os calendários']}
												label="Adicionar ao calendário"
												trigger="click"
												listStyle='modal'
												pastDateHandling='hide'
												buttonStyle='round'
												styleLight="--btn-background: rgb(15 29 215); --btn-text: #fff; --font: var(--font-inter); --btn-shadow: none; --btn-shadow-hover: none; --btn-text-hover: #fff; --btn-background-hover: rgb(44, 30, 91);"
												styleDark="--btn-background: rgb(15 29 215); --btn-text: #fff; --font: var(--font-inter); --btn-shadow: none; --btn-shadow-hover: none; --btn-text-hover: #fff; --btn-background-hover: rgb(44, 30, 91);"
												language='pt'
											/>
										) :(
											talk.videoId ? <Button variant="secondary" type='link' href={`https://www.youtube.com/watch?v=${talk.videoId}`}>
												Assistir gravação
											</Button> : null
										)}
									</div>
								</div>
							))}
						</Tabs.Content>
					))}
				</Tabs.Root>
			</div>
		</Section>
	);
};

export default Schedule;
